import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { OddsFormat } from "@/pages/Sport/utils/oddsFormat";

/** How a slip treats a price that moved since the player picked it (Betaxis applies it). */
export type OddsChangePolicy = "none" | "higher" | "any";

export interface SportPrefsState {
  oddsFormat: OddsFormat;
  acceptOddsChange: OddsChangePolicy;
  /** The stake a quick bet and the "default" chip use. */
  defaultStake: number;
  /** One tap on a price places a single at the default stake (after a short countdown). */
  quickBet: boolean;
  /** The last stakes placed, newest first, for the quick-stake chips. */
  recentStakes: number[];
}

const RECENT_STAKES = 4;

const initialState: SportPrefsState = {
  oddsFormat: "decimal",
  acceptOddsChange: "none",
  defaultStake: 10,
  quickBet: false,
  recentStakes: [],
};

/** The player's sportsbook display and slip preferences, kept in this browser (persisted). */
const sportPrefsSlice = createSlice({
  name: "sportPrefs",
  initialState,
  reducers: {
    setOddsFormat(state, action: PayloadAction<OddsFormat>) {
      state.oddsFormat = action.payload;
    },
    setAcceptOddsChange(state, action: PayloadAction<OddsChangePolicy>) {
      state.acceptOddsChange = action.payload;
    },
    setDefaultStake(state, action: PayloadAction<number>) {
      const value = Number(action.payload);
      if (Number.isFinite(value) && value > 0) state.defaultStake = Math.round(value * 100) / 100;
    },
    setQuickBet(state, action: PayloadAction<boolean>) {
      state.quickBet = action.payload;
    },
    rememberStake(state, action: PayloadAction<number>) {
      const value = Math.round(Number(action.payload) * 100) / 100;
      if (!Number.isFinite(value) || value <= 0) return;
      state.recentStakes = [value, ...(state.recentStakes ?? []).filter((s) => s !== value)].slice(
        0,
        RECENT_STAKES,
      );
    },
  },
});

export default sportPrefsSlice.reducer;

export const { setOddsFormat, setAcceptOddsChange, setDefaultStake, setQuickBet, rememberStake } =
  sportPrefsSlice.actions;
